import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import SignalBadge from "@/components/SignalBadge";
import { calcBreakEven, formatRoas, calcSignalClient } from "@/lib/signal";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function WeeklyLog() {
  const [brand, setBrand] = useState(null);
  const [spend, setSpend] = useState("");
  const [revenue, setRevenue] = useState("");
  const [sp, setSp] = useState("");
  const [cogs, setCogs] = useState("");
  const [other, setOther] = useState("");
  const [recentBuffers, setRecentBuffers] = useState([]);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [b, l] = await Promise.all([api.get("/brand"), api.get("/logs")]);
      if (!b.data) {
        navigate("/onboarding/brand");
        return;
      }
      setBrand(b.data);
      setSp(String(b.data.selling_price));
      setCogs(String(b.data.cogs));
      setOther(String(b.data.other_costs || 0));
      const buffers = (l.data || []).slice(-2).map((x) => x.buffer);
      setRecentBuffers(buffers);
    })().catch(() => toast.error("Failed to load"));
  }, [navigate]);

  const be = useMemo(
    () => calcBreakEven(Number(sp) || 0, Number(cogs) || 0, Number(other) || 0),
    [sp, cogs, other]
  );
  const roas = useMemo(() => (Number(spend) > 0 ? Number(revenue) / Number(spend) : 0), [spend, revenue]);
  const buffer = roas - be;
  const sig = calcSignalClient(buffer, [...recentBuffers, buffer]);

  const submit = async (e) => {
    e.preventDefault();
    if (!spend || !revenue) {
      toast.error("Ad spend and revenue are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/logs", {
        ad_spend: Number(spend),
        revenue: Number(revenue),
        selling_price: Number(sp),
        cogs: Number(cogs),
        other_costs: Number(other) || 0,
      });
      toast.success("Logged. Heading to dashboard…");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save log");
    } finally {
      setSaving(false);
    }
  };

  if (!brand) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">Log this week</h1>
        <p className="mt-2 text-slate-600">Enter your numbers. We'll recompute your signal.</p>

        <Card className="mt-8 p-6 sm:p-8 bg-white rounded-2xl border-slate-200">
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-5">
            <Money id="wl-spend" label="Ad spend" value={spend} onChange={setSpend} testid="weeklylog-spend-input" />
            <Money id="wl-rev" label="Revenue" value={revenue} onChange={setRevenue} testid="weeklylog-revenue-input" />
            <Money id="wl-sp" label="Selling price" value={sp} onChange={setSp} testid="weeklylog-sp-input" />
            <Money id="wl-cogs" label="COGS" value={cogs} onChange={setCogs} testid="weeklylog-cogs-input" />
            <Money id="wl-other" label="Other costs" value={other} onChange={setOther} testid="weeklylog-other-input" />
            <div className="grid grid-cols-3 gap-3 md:col-span-1">
              <Stat label="ROAS" value={formatRoas(roas)} />
              <Stat label="B/E" value={formatRoas(be)} />
              <Stat label="Buffer" value={(buffer >= 0 ? "+" : "") + formatRoas(buffer)} accent={buffer >= 0 ? "text-emerald-600" : "text-red-600"} />
            </div>
            <div className="md:col-span-2 mt-2 flex items-center justify-between flex-wrap gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <span className="text-sm text-slate-600">Preview signal</span>
              <SignalBadge signal={sig} size="lg" />
            </div>
            <div className="md:col-span-2">
              <Button type="submit" disabled={saving} className="w-full h-12 bg-slate-900 hover:bg-slate-800" data-testid="weeklylog-submit-button">
                {saving ? "Saving…" : (<><Save className="mr-2 h-4 w-4" /> Save this week</>)}
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
}

const Money = ({ id, label, value, onChange, testid }) => (
  <div>
    <Label htmlFor={id} className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</Label>
    <div className="relative mt-2">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
      <Input id={id} type="number" step="0.01" min="0" value={value} onChange={(e) => onChange(e.target.value)} className="pl-7 h-11" data-testid={testid} />
    </div>
  </div>
);

const Stat = ({ label, value, accent = "text-slate-900" }) => (
  <div className="rounded-lg border border-slate-200 p-3">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
    <p className={`font-display font-bold text-lg mt-0.5 ${accent}`}>{value}</p>
  </div>
);
